import { BuildingOfficeIcon, DocumentIcon, UserIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { FormTemplate } from "../../db/schema";
import { isTemplateProcessing } from "../../utilities/formTemplates";
import Button from "../common/Button";
import ListPage, { ListColumn } from "../common/ListPage";

// The officer's form templates (personal and department) to choose from, on the
// same ListPage and selection the other pickers use. A case often needs several
// forms, so any number can be ticked (up to MAX_FORMS); or click a form to look
// at it first.
export interface TemplatesPickerOptions {
  confirmLabel: (count: number) => string;
  onConfirm: (templates: FormTemplate[]) => void;
  onOpenTemplate: (template: FormTemplate) => void;
  // The ticked forms live in the host (the modal), which decides what happens to
  // them when the officer looks at a form and comes back.
  selected: Map<string, FormTemplate>;
  onSelectedChange: (next: Map<string, FormTemplate>) => void;
}

export const MAX_FORMS = 10;

// Why a template cannot be filled right now (it has nowhere to put anything).
export const templateUnusableReason = (t: FormTemplate): string | undefined => {
  if (isTemplateProcessing(t)) return "Still being read. Try again in a moment.";
  if (!t.fields?.length) return "This form has no fields yet.";
  return undefined;
};

const TemplatesPickerList: React.FC<{ picker: TemplatesPickerOptions }> = ({ picker }) => {
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { selected, onSelectedChange } = picker;
  const atLimit = selected.size >= MAX_FORMS;

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/api/forms/templates")
      .then((res) => !cancelled && setTemplates(res.data || []))
      .catch((error) => {
        console.error("Error loading form templates:", error);
        if (!cancelled) setTemplates([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => !q || t.name.toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q));
  }, [templates, search]);

  const columns: ListColumn<FormTemplate, "name">[] = [
    {
      key: "name",
      header: "Form",
      render: (t) => (
        <div className="flex items-center gap-2 min-w-0">
          <DocumentIcon className="h-5 w-5 text-primary shrink-0" />
          <div className="min-w-0">
            <p className="font-medium truncate m-0">{t.name}</p>
            {t.description && <p className="text-xs text-base-content/40 truncate max-w-md m-0">{t.description}</p>}
          </div>
        </div>
      ),
    },
    { key: "fields", header: "Fields", className: "text-sm text-base-content/60", render: (t) => t.fields?.length || 0 },
    {
      key: "scope",
      header: "Scope",
      render: (t) => (
        <span className="inline-flex items-center gap-1 text-xs text-base-content/60 whitespace-nowrap">
          {t.scope === "agency" ? <BuildingOfficeIcon className="h-3.5 w-3.5" /> : <UserIcon className="h-3.5 w-3.5" />}
          {t.scope === "agency" ? "Dept" : "Personal"}
        </span>
      ),
    },
  ];

  return (
    <ListPage<FormTemplate, "name">
      title="Choose forms"
      subtitle="Tick every form to fill out from this case, or click a form to look at it first."
      items={shown}
      getKey={(t) => t.id}
      columns={columns}
      onRowClick={picker.onOpenTemplate}
      search={{ value: search, onChange: setSearch, placeholder: "Search forms..." }}
      selection={{
        selected: new Set(selected.keys()),
        onToggle: (t) => {
          const next = new Map(selected);
          if (next.has(t.id)) next.delete(t.id);
          else if (next.size < MAX_FORMS) next.set(t.id, t);
          onSelectedChange(next);
        },
        noun: "form",
        note: atLimit ? `Up to ${MAX_FORMS} forms at a time` : undefined,
        disabledReason: (t) =>
          templateUnusableReason(t) ?? (!selected.has(t.id) && atLimit ? `Up to ${MAX_FORMS} forms at a time` : undefined),
        actions: (
          <>
            <Button variant="primary" size="sm" onClick={() => picker.onConfirm(Array.from(selected.values()))}>
              {picker.confirmLabel(selected.size)}
            </Button>
            <Button variant="clean" size="sm" onClick={() => onSelectedChange(new Map())}>
              Clear
            </Button>
          </>
        ),
      }}
      loading={loading && templates.length === 0}
      isFiltered={Boolean(search.trim())}
      noMatch="No forms match your search."
      empty={{ icon: <DocumentIcon className="h-12 w-12" />, title: "No forms yet", description: "Upload a form on the Forms page, then come back to fill it from this case." }}
    />
  );
};

export default TemplatesPickerList;
